// Reglas de la página de equipo: qué temporada mostrar y en qué orden van sus competiciones.
//
// Port del fix de Olé "priorizar torneos vigentes en fichas de equipo" (2ab87c146).
// El problema que resuelve: el orden anterior ponía primero las competiciones que están
// en el menú del CMS, en ese orden, sin mirar si el torneo terminó. Así una Libertadores
// ya jugada podía aparecer arriba del torneo que el equipo está jugando hoy.

const isCalendarActiveFlag = (calendar) => {
    const active = calendar?.active;
    return active === true || active === "yes" || active === "true" || active === 1;
};

const getTodayIsoDate = () => new Date().toISOString().slice(0, 10);

/** Si el torneo está en curso: por flag de Opta, por fecha de fin, o por año en el nombre. */
export const isTournamentCalendarCurrent = (calendar) => {
    if (!calendar) return false;
    if (isCalendarActiveFlag(calendar)) return true;

    const today = getTodayIsoDate();
    if (calendar.endDate && calendar.endDate >= today) return true;

    const currentYear = String(new Date().getFullYear());
    if (calendar.name && String(calendar.name).includes(currentYear)) return true;

    return false;
};

/**
 * Temporada a mostrar de una competición del equipo, en cascada: la marcada como activa,
 * la que esté abierta (prefiriendo la ya empezada), la del año actual, y si nada aplica
 * la primera. Reemplaza al `tournamentCalendar[0]` que se usaba antes.
 */
export const selectTeamTournamentCalendar = (competition) => {
    const calendars = competition?.tournamentCalendar;
    if (!Array.isArray(calendars) || calendars.length === 0) {
        return null;
    }

    const today = getTodayIsoDate();
    const currentYear = String(new Date().getFullYear());

    const activeCalendar = calendars.find(isCalendarActiveFlag);
    if (activeCalendar) return activeCalendar;

    const openCalendars = calendars.filter((tc) => tc?.endDate && tc.endDate >= today);
    if (openCalendars.length > 0) {
        const inProgress = openCalendars
            .filter((tc) => !tc.startDate || tc.startDate <= today)
            .sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
        if (inProgress.length > 0) return inProgress[0];
        return openCalendars.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""))[0];
    }

    const byYear = calendars.find((tc) => tc?.name && String(tc.name).includes(currentYear));
    if (byYear) return byYear;

    return calendars[0];
};

/**
 * Menor puntaje = más arriba en la ficha de equipo.
 * Los torneos vigentes respetan el orden del menú del CMS; los terminados van después.
 */
export const getTeamCompetitionSortPriority = (competitionId, options = {}) => {
    const { isCurrent = true, featuredCompetitions = [] } = options;
    const featuredIndex = featuredCompetitions.findIndex((item) => item.id === competitionId);
    const historicalOffset = isCurrent ? 0 : 1000;

    if (featuredIndex !== -1) {
        return historicalOffset + featuredIndex;
    }

    // Las que no están en el menú quedan detrás de las que sí, dentro del mismo bloque
    return historicalOffset + featuredCompetitions.length + 1;
};

/** Ordena los bloques de fixture del equipo: vigentes primero, terminados después. */
export const compareTeamFixtureBlocks = (a, b, options = {}) => {
    const { featuredCompetitions = [], calendarsByCompetitionId } = options;
    const infoA = a?.match?.[0]?.matchInfo;
    const infoB = b?.match?.[0]?.matchInfo;
    const idA = infoA?.competition?.id;
    const idB = infoB?.competition?.id;

    const calendarMetaA = calendarsByCompetitionId?.[idA]
        ? selectTeamTournamentCalendar(calendarsByCompetitionId[idA])
        : infoA?.tournamentCalendar;
    const calendarMetaB = calendarsByCompetitionId?.[idB]
        ? selectTeamTournamentCalendar(calendarsByCompetitionId[idB])
        : infoB?.tournamentCalendar;

    const currentA = isTournamentCalendarCurrent(calendarMetaA);
    const currentB = isTournamentCalendarCurrent(calendarMetaB);

    const priorityA = getTeamCompetitionSortPriority(idA, { featuredCompetitions, isCurrent: currentA });
    const priorityB = getTeamCompetitionSortPriority(idB, { featuredCompetitions, isCurrent: currentB });

    if (priorityA !== priorityB) return priorityA - priorityB;

    // Dentro del bloque de terminados: temporadas más recientes primero
    if (!currentA && !currentB) {
        const endA = calendarMetaA?.endDate || calendarMetaA?.name || "";
        const endB = calendarMetaB?.endDate || calendarMetaB?.name || "";
        return String(endB).localeCompare(String(endA));
    }

    return 0;
};
